/**
 * Client wrapper for the v4 FuelJourney API (`/api/journeys`). Mirrors
 * `trainingPlanApi.ts` (same `apiCall` / CSRF-refresh helpers). See the server
 * routes in `src/modules/journeys/routes.ts`.
 */
import { apiCall, apiCallWithCsrfRefresh, ApiError, isAbortError } from "./api";
import { getAnonPendingRaceEvents } from "./anonDraft";
import { anonDraftPreferencesForGenerate } from "./preferencesApi";
import type { PlanInputFreshness } from "./fuelPlanTypes";

const BASE = "/api/journeys";

/**
 * Shared POST body for the journey generate surfaces: anon lead `email`,
 * `force` (stale regenerate), `discardCustomizations` (F-11-1 — the customer
 * confirmed a forced rebuild of a box carrying edits), and the Slice C anon
 * draft `preferences` (a
 * guest's dietary needs / exclusions must reach the engine payload — see
 * anonDraftPreferencesForGenerate). Older servers strip unknown keys (zod
 * passthrough), so each field is safe to send ahead of its server lane;
 * undefined when there is nothing to carry.
 */
function generateBodyWithAnonExtras(
  email?: string,
  force?: boolean,
  discardCustomizations?: boolean
): Record<string, unknown> | undefined {
  const preferences = anonDraftPreferencesForGenerate();
  if (!email && !force && !preferences && !discardCustomizations) return undefined;
  return {
    ...(email ? { email } : {}),
    ...(force ? { force } : {}),
    ...(discardCustomizations ? { discardCustomizations } : {}),
    ...(preferences ? { preferences } : {})
  };
}

export type DropUiState = "delivered" | "fueled" | "ready" | "upcoming" | "skipped";

export type DropWindow = {
  index: number;
  startYmd: string;
  endYmd: string;
  weekRange: [number, number];
  /** Plan-group workouts inside the window — the set a generate fuels. */
  workoutCount?: number;
  /** Ids of those workouts — the only sessions a box card may list. */
  workoutIds?: string[];
  /** Average planned training minutes per window week; null when unscheduled. */
  avgWeeklyMinutes?: number | null;
};

/** "8 workouts" / "1 workout" — null when the server sent no stats. */
export function dropWorkoutsLabel(drop: DropWindow): string | null {
  if (drop.workoutCount == null || drop.workoutCount === 0) return null;
  return `${drop.workoutCount} workout${drop.workoutCount === 1 ? "" : "s"}`;
}

export type DropStateRow = {
  index: number;
  state: DropUiState;
  isSequenceActive: boolean;
  isCtaActive: boolean;
  /** The drop's generated FuelPlan id when one exists (nav chips / plan links). */
  planId?: string | null;
  /** Journeys-overhaul P4: workouts changed after this box generated — offer regenerate.
   *  Superseded by {@link freshness} (kept for back-compat with older reads). */
  isStale?: boolean;
  /** Unified plan-input freshness for this box (mirrors server `planFreshness.ts`).
   *  Present only when the box has a generated plan. The shared
   *  `PlanFreshnessPrompt` renders from `freshness.reasons`. */
  freshness?: PlanInputFreshness;
  /** F-11-1: the box's plan carries customer edits. The stale-chip Regenerate is
   *  a `force` generate, which mints a fresh plan and drops the overlay — so the
   *  rail confirms first and then sends `discardCustomizations`. Absent on boxes
   *  with no plan, on paid boxes (frozen) and on an empty overlay. Optional
   *  until the server sends it. */
  hasCustomizations?: boolean;
  /** The window's best candidate when it is expired — stored, or effectively so
   *  (past TTL before the sweep ran). The box has no live plan then (`state`
   *  stays `"upcoming"`), so the card names the date and offers a rebuild
   *  instead of a bare "generate". Mirrors server `DropStateRow`. */
  expiredPlanId?: string | null;
  /** That plan's TTL deadline, ISO across the wire. */
  expiredAt?: string | null;
};

/** Journeys-overhaul P5: a tune-up (intent=milestone) event inside the journey window,
 *  rendered inline on the timeline with its own race-day kit. */
export type JourneyMilestone = {
  eventId: string;
  name: string;
  ymd: string;
  distanceLabel?: string | null;
  /** The milestone's race-day-kit FuelPlan when generated. */
  previewPlanId?: string | null;
  /** Workouts/date/prefs changed on the prep race after its kit generated — the
   *  card offers regenerate (mirrors the goal `raceDayPreviewStale`). Optional
   *  until the server sends it. */
  previewStale?: boolean;
  /** Unified plan-input freshness for the milestone kit (mirrors the goal
   *  `raceDayPreviewFreshness`) — nullable; present only when a completed prep-race
   *  kit exists. Supersedes `previewStale` for a render reason. */
  previewFreshness?: PlanInputFreshness | null;
};

/** EVENT-scoped targets only — athlete per-sport targets live on the fueling
 *  profile (`fuelingProfileApi.ts`). */
export type CustomTargets = {
  raceDayGramsPerHour?: number;
  perWorkout?: Record<string, number>;
  /** Event-scoped race-day sodium override, mg/hr (fuel-targets refactor A5).
   *  Rides through the same `customTargets` passthrough as the carb anchor;
   *  when unset the race-day sodium falls back to the athlete `sodiumTarget`. */
  sodium?: { raceDay?: number };
};

export type JourneyView = {
  id: string;
  status: string;
  customerEventId: string | null;
  trainingPlanId: string | null;
  dropWeeks: number | null;
  /** ATHLETE ramp (FuelingProfile) — identical on every journey view (one
   *  gut). Edit via `/api/fueling-profile`; kept here so rails render with no
   *  extra fetch. */
  rampEnabled: boolean;
  /** Server-computed GUT-TRAINING position — the ONE source for
   *  "Gut-training week N of M" (F2/D9). pending = starts in the future;
   *  full = at 100%. Null = ramp off. */
  rampState: "pending" | "active" | "full" | null;
  rampWeekNow: number | null;
  rampWeeksTotal: number | null;
  /** Canonical server-rendered ramp label ("Gut-training week N of M") — the
   *  ONE string surfaces should print (C11); never re-derive week N from a
   *  date. Optional until the server sends it — callers fall back to composing
   *  from `rampWeekNow`/`rampWeeksTotal`. */
  rampWeekLabel?: string | null;
  /** YMD the ramp reaches full carbs (jump-adjusted). */
  rampFullYmd: string | null;
  /** The ramp's calendar anchor (profile startedYmd) — the grid for
   *  `gutTrainingWeekFor` offsets. Null when the ramp is off. */
  rampStartedYmd: string | null;
  /** YMD of PLAN week 1 day 1 — anchor for per-week calendar annotations
   *  (race-anchored for library plans, plan start otherwise). NOT the ramp. */
  planWeekAnchorYmd: string | null;
  /** Profile-derived (beginner hints). */
  gutLevel: string | null;
  sodiumTarget: number | null;
  customTargets: CustomTargets | null;
  raceDayPreviewPlanId: string | null;
  /** Workouts changed on race day after the goal kit generated — the race-day
   *  card offers regenerate (mirrors DropStateRow.isStale). Optional until the
   *  server sends it. */
  raceDayPreviewStale?: boolean;
  /** Unified plan-input freshness for the goal race-day kit (mirrors server
   *  `planFreshness.ts`) — supersedes `raceDayPreviewStale`, adding profile /
   *  targets / preferences drift. Present only when a completed goal kit exists. */
  raceDayPreviewFreshness?: PlanInputFreshness;
  /** Race header context (1f). Null name for anon — fall back to the pending-race draft. */
  raceName: string | null;
  raceYmd: string | null;
  planStartYmd: string | null;
  planWeeks: number | null;
  goalFinish: string | null;
  /** Applied plan's distance ('marathon' | 'half' | …) for the settings method row. */
  planDistance: string | null;
  /** Applied plan's workout group — matches drop windows to calendar chips. */
  planGroupId: string | null;
  drops: DropWindow[];
  dropStates: DropStateRow[];
  /** Journeys-overhaul contract (fields land server-side in phases; optional until then). */
  /** Discriminator: race journeys end on raceYmd; rolling journeys derive forward windows
   *  from anchorYmd with no endpoint (P3). Absent ⇒ "race". */
  journeyType?: "race" | "rolling";
  /** Rolling journey's forward-derivation anchor (P3). Null/absent on race journeys. */
  anchorYmd?: string | null;
  /** Anchor P8 — set on an ACTIVE race journey whose FIRST box holds no
   *  workouts while training starts later in the window: the first workout's
   *  YMD. The timeline offers a one-tap "align start" (PATCH startYmd).
   *  Null/absent when aligned, pinned by a generated/paid box, rolling, or
   *  the edit would be window_locked (library plan + later start). */
  startAlignYmd?: string | null;
  /** Always null since the workout-anchored box chain (rolling journeys no
   *  longer pause under a race); kept for server response compatibility. */
  pausedBy?: {
    journeyId: string | null;
    raceName: string | null;
    raceYmd: string;
    resumeYmd: string;
  } | null;
  /** Always null since the workout-anchored box chain; kept for server
   *  response compatibility. */
  resumeAfterYmd?: string | null;
  /** Tune-up events inside the window (P5) — each with its kit if generated.
   *  Always [] for rolling journeys and anon principals. */
  milestones?: JourneyMilestone[];
  /**
   * Journey-focus plan §3.1: the UTC calendar day this race journey becomes
   * eligible for natural completion (race day + grace), computed SERVER-side
   * with the same UTC helper the sweep uses — the client must never derive the
   * countdown itself. Null for rolling journeys / no race date; optional until
   * the server sends it. No UI usage yet (Wave 3 renders the grace countdown).
   */
  naturalCompletionEligibleYmd?: string | null;
};

/** What a journey action is busy building: a drop index, the goal race-day
 *  preview, or a milestone kit (`milestone:{eventId}` — P5). */
export type JourneyBusy = number | "preview" | `milestone:${string}` | null;

export type CreateJourneyBody = {
  method: "fuel_goods_plan" | "import" | "build";
  planId?: string;
  raceDate?: string;
  raceName?: string;
  eventCatalogId?: string;
  distanceLabel?: string;
  customerEventId?: string;
  dropWeeks?: number | null;
  /** Rolling journeys only: weeks of coverage to tile forward initially (the
   *  athlete's setup pick). Server derives the forward horizon from it; null ⇒
   *  the legacy fixed 3-window horizon. Ignored on race journeys. */
  coverageWeeks?: number | null;
  /** Event-scoped only ({ raceDayGramsPerHour?, perWorkout? }) — athlete
   *  fields (sodium/gut/ramp/per-sport) PATCH `/api/fueling-profile` first. */
  customTargets?: unknown;
  /** Journeys-overhaul contract (accepted server-side from P1/P2). */
  /** Stored window start (J2). Defaulted server-side from distance build length when
   *  omitted; our-plan journeys derive it from the plan. */
  startYmd?: string;
  /** "rolling" creates an endpoint-less training journey (P3). Absent ⇒ "race". */
  journeyType?: "race" | "rolling";
  /** How to resolve a 409 journey_overlap on retry (P2, J4): chain = start after the
   *  existing race; split = shorten the existing window; demote = existing event becomes
   *  a milestone (its journey archives). Executed transactionally server-side. */
  overlapResolution?: { mode: OverlapMode };
  /** Multi-conflict resolutions (robustness spec §6.3): the ACCUMULATED picks
   *  across successive `journey_overlap` 409s, each naming the journey it
   *  resolves so a stale pick can never archive the wrong build. The server
   *  applies all of them and re-runs the gate; the legacy singular
   *  `overlapResolution` stays accepted (treated as the first conflict's). */
  overlapResolutions?: Array<{ journeyId: string; mode: OverlapMode }>;
  /** `nest_new` resolution (S1, spec §7.1): retry the create with this flag to mark the
   *  NEW event a milestone and create NO journey. The server ensures the CustomerEvent
   *  exists with `intent:"milestone"` (creating it if `customerEventId` is absent) and
   *  returns the distinct `{ milestone: true, event }` shape — NOT a journey. */
  resolveAsMilestone?: boolean;
};

export type JourneyPatch = {
  dropWeeks?: number | null;
  customTargets?: unknown;
  status?: "active" | "archived" | "completed";
  /** Journeys-overhaul contract (P2): window edits — non-our-plan journeys only (J2). */
  startYmd?: string;
  raceYmd?: string;
  /**
   * Role-conflict resolutions (C3, spec §9.2/§9.3) the client sends alongside a
   * window/status change to answer an S3 `409`. Applied in the SAME transaction:
   *   - `reclassify`      — a covered BARE event (extend) or an orphaned tune-up
   *                          (shrink): `to:"milestone"` files it as a tune-up here,
   *                          `to:"goal"` reverts a tune-up to its own goal.
   *   - `pairResolution`  — a covered GOAL-with-journey event (extend, Gap 3):
   *                          `demote` nests the covered goal; `split` truncates it.
   *   - `clampStartYmd`   — "stop the window before it" (extend) / "extend to keep
   *                          it" (shrink) — the clamped/widened start.
   *   - `tuneupDisposition`— archive of a goal build with nested tune-ups (Gap 1):
   *                          `keep_as_goal` reverts each, `remove` deletes them + kits.
   */
  reclassify?: Array<{ eventId: string; to: "milestone" | "goal" }>;
  pairResolution?: Array<{ eventId: string; mode: "demote" | "split" }>;
  clampStartYmd?: string;
  tuneupDisposition?: "keep_as_goal" | "remove";
};

/**
 * Role/conflict outcomes (spec §7, shared geometry `@shared/modules/journeys/
 * collisionGeometry`). Kept in sync with `CollisionOutcome` — re-exported here
 * so client code can name outcomes without pulling in `@shared` for the type.
 *   - `nest_new`      → mark the newly-added event a milestone, don't create its journey
 *   - `nest_existing` → wire mode `"demote"`: archive the existing journey, its event → milestone
 *   - `chain` / `split`
 * The on-the-wire `overlapResolution.mode` stays `"chain" | "split" | "demote"`
 * (`nest_existing` maps to `"demote"`); `nest_new` is a create-time outcome, not
 * an overlap mode — it retries create with `resolveAsMilestone: true` (spec §2). */
export type RoleOutcome = "nest_new" | "nest_existing" | "chain";

/** The wire mode a create-path `overlapResolution` carries. `nest_existing`
 *  → `"demote"`. (`split` was removed from the create/pair path; the geometry
 *  now returns only `[nest_new | nest_existing, "chain"]`.) */
export type OverlapMode = "chain" | "demote";

/** A minimal event reference the RoleResolutionSheet + resolution flows pass
 *  around (spec §11.8). `hasJourney` = the event already anchors a build. */
export type EventRef = {
  id: string;
  name: string;
  ymd: string;
  hasJourney: boolean;
};

/** 409 body when a goal journey's window overlaps an existing active journey (P2, J4).
 *  Retry the create with `overlapResolution` set to one of `resolutions`
 *  (S1 extends the set with `nest_new`: retry create with `resolveAsMilestone: true`). */
export type JourneyOverlapConflict = {
  error: "journey_overlap";
  existing: {
    journeyId: string;
    raceName: string | null;
    raceYmd: string | null;
    startYmd: string | null;
    /** S1: the existing event already anchors a build. Defaults true (it 409'd on overlap). */
    hasJourney?: boolean;
    /** §6.1 stitch (ADDITIVE — absent on an old server): the EVENT anchoring
     *  the conflicting journey (null for anon/event-less builds). Gates the
     *  sheet's "Edit {race}'s date instead" hatch together with
     *  `eventCatalogId` (null ⇒ CUSTOM ⇒ editable). */
    customerEventId?: string | null;
    eventCatalogId?: string | null;
  };
  /** S1: the newly-added event's context, so the sheet can name it + order the pair. */
  newEvent?: {
    id?: string | null;
    name?: string | null;
    ymd?: string | null;
  } | null;
  resolutions: RoleOutcome[];
  /** §6.3 (ADDITIVE — absent on an old server): how many journeys STILL
   *  actively collide at this 409. `existing` remains the first by race date;
   *  the client loops sheet → accumulate → retry until this reaches zero. */
  conflictCount?: number;
  /** G5 (ADDITIVE): what each resolution would do to the FIRST conflict —
   *  parsed/threaded now, rendered by a later task. */
  consequences?: {
    demote?: { removedSessions: number };
    split?: { removedSessions: number };
    chain?: { newStartYmd: string; removedSessions?: number };
  } | null;
};

/** Coerce an arbitrary array to the valid `RoleOutcome[]`, dropping unknowns. */
function parseRoleOutcomes(raw: unknown): RoleOutcome[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(
      (r): r is RoleOutcome | "demote" =>
        r === "nest_new" || r === "nest_existing" || r === "chain" || r === "demote"
    )
    // Server may still emit the wire mode `"demote"` for back-compat — normalize
    // it to the outcome name the sheet renders. `split` is no longer a pair
    // outcome (the geometry returns only `[nest_new | nest_existing, "chain"]`);
    // any stray `split` on the create/pair path is dropped by the filter above.
    .map((r): RoleOutcome => (r === "demote" ? "nest_existing" : r));
}

/**
 * Extract the structured overlap conflict from a failed journey create, or null
 * for any other error. The server 409s with this body from P2; S1 adds
 * `nest_new` + the `newEvent` context. Back-compat: a legacy body sending
 * `resolutions:["demote"]` is normalized to `nest_existing`.
 */
export function journeyOverlapConflictFromError(err: unknown): JourneyOverlapConflict | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const body = err.body as
    | (Partial<JourneyOverlapConflict> & { resolutions?: unknown })
    | null;
  if (!body || typeof body !== "object" || body.error !== "journey_overlap") return null;
  if (!body.existing || typeof body.existing !== "object") return null;
  const resolutions = parseRoleOutcomes(body.resolutions);
  if (resolutions.length === 0) return null;
  const ex = body.existing;
  const ne = body.newEvent ?? null;
  return {
    error: "journey_overlap",
    existing: {
      journeyId: String(ex.journeyId ?? ""),
      raceName: ex.raceName ?? null,
      raceYmd: ex.raceYmd ?? null,
      startYmd: ex.startYmd ?? null,
      hasJourney: ex.hasJourney ?? true,
      // §6.1 additive identity fields — tolerate absence (old server) or junk.
      customerEventId:
        typeof ex.customerEventId === "string" && ex.customerEventId
          ? ex.customerEventId
          : null,
      eventCatalogId:
        typeof ex.eventCatalogId === "string" && ex.eventCatalogId
          ? ex.eventCatalogId
          : null
    },
    newEvent: ne
      ? { id: ne.id ?? null, name: ne.name ?? null, ymd: ne.ymd ?? null }
      : null,
    resolutions,
    // §6.3 additive fields — tolerate their absence (old server) or junk.
    conflictCount:
      typeof body.conflictCount === "number" && Number.isFinite(body.conflictCount)
        ? body.conflictCount
        : undefined,
    consequences: parseOverlapConsequences(body.consequences)
  };
}

/** Loose passthrough of the §G5 `consequences` block — keep only the known,
 *  well-typed keys so a malformed server payload can't leak into the UI. */
function parseOverlapConsequences(
  raw: unknown
): JourneyOverlapConflict["consequences"] {
  if (!raw || typeof raw !== "object") return undefined;
  const c = raw as {
    demote?: { removedSessions?: unknown } | null;
    split?: { removedSessions?: unknown } | null;
    chain?: { newStartYmd?: unknown; removedSessions?: unknown } | null;
  };
  const out: NonNullable<JourneyOverlapConflict["consequences"]> = {};
  if (c.demote && typeof c.demote.removedSessions === "number") {
    out.demote = { removedSessions: c.demote.removedSessions };
  }
  if (c.split && typeof c.split.removedSessions === "number") {
    out.split = { removedSessions: c.split.removedSessions };
  }
  if (c.chain && typeof c.chain.newStartYmd === "string") {
    out.chain = {
      newStartYmd: c.chain.newStartYmd,
      ...(typeof c.chain.removedSessions === "number"
        ? { removedSessions: c.chain.removedSessions }
        : {})
    };
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** 409 body when a window PATCH would newly cover outside events (S3, spec §9.2, T4).
 *  Per-event resolutions differ by `hasJourney` (Gap 3): a bare event offers
 *  `["reclassify","clamp_start"]`; a goal-with-journey offers the pair outcomes
 *  `["nest_existing","split","clamp_start"]`. */
export type WindowCoversConflict = {
  error: "window_covers_events";
  journeyId: string;
  events: Array<{
    id: string;
    name: string;
    /** Server key is `date`; parsed to `ymd` here for a single event shape. */
    ymd: string;
    hasJourney: boolean;
    resolutions: string[];
  }>;
};

/** 409 body when a window shrink would push a nested tune-up outside `[start,race)`
 *  (S3, spec §9.2, T10). Purchased/delivered kits ride through `hasPurchasedKit`
 *  so the sheet can warn (SF16). */
export type WindowOrphansConflict = {
  error: "window_orphans_tuneups";
  journeyId: string;
  events: Array<{
    id: string;
    name: string;
    ymd: string;
    hasPurchasedKit?: boolean;
  }>;
};

/** 409 body when archiving a goal journey whose window still holds nested
 *  tune-ups (S3, spec §9.3, Gap 1). Answered with `tuneupDisposition` on the
 *  same PATCH; `keep_as_goal` reverts each to its own goal, `remove` deletes. */
export type GoalArchiveTuneupsConflict = {
  error: "goal_archive_tuneups";
  journeyId: string;
  tuneups: Array<{
    id: string;
    name: string;
    ymd: string;
    hasPurchasedKit?: boolean;
  }>;
};

/** 409 body when a promote (milestone→goal) collides (S2, spec §9.4). Mirrors
 *  `journey_overlap` so the SAME `pair` sheet resolves it. */
export type NeedsCollisionResolution = {
  needsCollisionResolution: true;
  conflicts: JourneyOverlapConflict;
};

function eventYmd(raw: { ymd?: unknown; date?: unknown }): string {
  const v = raw.ymd ?? raw.date;
  return typeof v === "string" ? v : "";
}

/** Extract a `window_covers_events` 409 (S3), or null for anything else. */
export function windowCoversFromError(err: unknown): WindowCoversConflict | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const body = err.body as
    | { error?: string; journeyId?: unknown; events?: unknown }
    | null;
  if (!body || typeof body !== "object" || body.error !== "window_covers_events") return null;
  if (!Array.isArray(body.events)) return null;
  const events = body.events
    .filter((e): e is Record<string, unknown> => !!e && typeof e === "object")
    .map((e) => ({
      id: String(e.id ?? ""),
      name: typeof e.name === "string" ? e.name : "",
      ymd: eventYmd(e as { ymd?: unknown; date?: unknown }),
      hasJourney: e.hasJourney === true,
      resolutions: Array.isArray(e.resolutions)
        ? e.resolutions.filter((r): r is string => typeof r === "string")
        : []
    }))
    .filter((e) => e.id);
  if (events.length === 0) return null;
  return { error: "window_covers_events", journeyId: String(body.journeyId ?? ""), events };
}

/** Extract a `window_orphans_tuneups` 409 (S3), or null for anything else. */
export function windowOrphansFromError(err: unknown): WindowOrphansConflict | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const body = err.body as
    | { error?: string; journeyId?: unknown; events?: unknown }
    | null;
  if (!body || typeof body !== "object" || body.error !== "window_orphans_tuneups") return null;
  if (!Array.isArray(body.events)) return null;
  const events = body.events
    .filter((e): e is Record<string, unknown> => !!e && typeof e === "object")
    .map((e) => ({
      id: String(e.id ?? ""),
      name: typeof e.name === "string" ? e.name : "",
      ymd: eventYmd(e as { ymd?: unknown; date?: unknown }),
      hasPurchasedKit: e.hasPurchasedKit === true
    }))
    .filter((e) => e.id);
  if (events.length === 0) return null;
  return { error: "window_orphans_tuneups", journeyId: String(body.journeyId ?? ""), events };
}

/** Extract a `goal_archive_tuneups` 409 (S3, spec §9.3), or null for anything else.
 *  The server key is `tuneups` (not `events`). */
export function goalArchiveTuneupsFromError(err: unknown): GoalArchiveTuneupsConflict | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const body = err.body as
    | { error?: string; journeyId?: unknown; tuneups?: unknown }
    | null;
  if (!body || typeof body !== "object" || body.error !== "goal_archive_tuneups") return null;
  if (!Array.isArray(body.tuneups)) return null;
  const tuneups = body.tuneups
    .filter((e): e is Record<string, unknown> => !!e && typeof e === "object")
    .map((e) => ({
      id: String(e.id ?? ""),
      name: typeof e.name === "string" ? e.name : "",
      ymd: eventYmd(e as { ymd?: unknown; date?: unknown }),
      hasPurchasedKit: e.hasPurchasedKit === true
    }))
    .filter((e) => e.id);
  if (tuneups.length === 0) return null;
  return { error: "goal_archive_tuneups", journeyId: String(body.journeyId ?? ""), tuneups };
}

/** Extract a promote-collision 409 (S2, `{ needsCollisionResolution, conflicts }`),
 *  or null, mapping it into the `journey_overlap` shape the pair sheet consumes.
 *
 *  The server (events/reclassifyEvent.ts) emits `conflicts` as an ARRAY of FLAT
 *  `PromoteCollision` `{ journeyId, raceName, raceYmd, startYmd, resolutions }`
 *  (one entry per containing goal build). The `pair` sheet resolves ONE
 *  collision at a time — multi-conflict resolves by retry (resolve
 *  `conflicts[0]` → re-POST `/:id/role` → the server returns the remaining
 *  conflicts) — so we map the FIRST entry. A legacy single-object `conflicts`
 *  (fields nested under `.existing`) is still tolerated so an old/new server
 *  pairing can't silently no-op the promote. */
export function needsCollisionResolutionFromError(
  err: unknown
): NeedsCollisionResolution | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const body = err.body as { needsCollisionResolution?: unknown; conflicts?: unknown } | null;
  if (!body || typeof body !== "object" || body.needsCollisionResolution !== true) return null;

  const rawConflicts = body.conflicts;
  const first = Array.isArray(rawConflicts) ? rawConflicts[0] : rawConflicts;
  if (!first || typeof first !== "object") return null;
  const c = first as {
    journeyId?: unknown;
    raceName?: unknown;
    raceYmd?: unknown;
    startYmd?: unknown;
    hasJourney?: unknown;
    resolutions?: unknown;
    // Legacy single-object shape nested the existing event under `.existing`.
    existing?: Record<string, unknown> | null;
    newEvent?: { id?: unknown; name?: unknown; ymd?: unknown } | null;
  };
  // Flat (server array entry) or legacy nested (`.existing`) — read from whichever.
  const ex =
    c.existing && typeof c.existing === "object"
      ? (c.existing as Record<string, unknown>)
      : (c as Record<string, unknown>);

  const resolutions = parseRoleOutcomes(c.resolutions);
  if (resolutions.length === 0) return null;
  const journeyId = typeof ex.journeyId === "string" ? ex.journeyId : "";
  if (!journeyId) return null;
  const asStr = (v: unknown): string | null => (typeof v === "string" ? v : null);
  const ne = c.newEvent ?? null;
  return {
    needsCollisionResolution: true,
    conflicts: {
      error: "journey_overlap",
      existing: {
        journeyId,
        raceName: asStr(ex.raceName),
        raceYmd: asStr(ex.raceYmd),
        startYmd: asStr(ex.startYmd),
        hasJourney: typeof ex.hasJourney === "boolean" ? ex.hasJourney : true
      },
      newEvent: ne
        ? { id: asStr(ne.id), name: asStr(ne.name), ymd: asStr(ne.ymd) }
        : null,
      resolutions
    }
  };
}

/** Drop-generate failure codes (P1): `empty_window` = no fuel-eligible workouts in the
 *  box's date window (D3 — add workouts, don't generate); `drop_locked` = sequence rule;
 *  `drop_not_generatable` = missing journey/drop; `email_required` = E-03-1 anon gate;
 *  `source_has_customizations` = F-11-1, a forced rebuild would clear the box's edits. */
export type DropGenerateErrorCode =
  | "empty_window"
  | "drop_locked"
  | "drop_not_generatable"
  | "email_required"
  | "source_has_customizations";

/**
 * Classify a drop-generate failure (P1 server contract): `422 empty_window`
 * (no fuel-eligible workouts in the box's window — nudge "add workouts"),
 * `409 drop_locked` (sequence rule), `404 drop_not_generatable` (missing
 * journey/drop), `400 email_required` (E-03-1 anon lead gate) or
 * `409 source_has_customizations` (F-11-1 — confirm before clearing edits).
 * Null for anything else (network, 5xx, aborts).
 */
export function dropGenerateErrorCode(err: unknown): DropGenerateErrorCode | null {
  if (!(err instanceof ApiError)) return null;
  if (err.code === "empty_window" || err.code === "drop_locked") return err.code;
  if (err.code === "drop_not_generatable") return err.code;
  if (err.code === "email_required" || err.code === "source_has_customizations") return err.code;
  return null;
}

/** Generate-endpoint result (mirrors the fuel-plan generate result). */
export type JourneyGenerateResult = {
  fuelPlanRequestId: string;
  status: "processing" | "completed";
  fuelPlanId: string | null;
  reused: boolean;
  /** The plan this build replaced, when it replaced one — see `GenerateResponse`. */
  sourcePlanId?: string | null;
};

/** The applied training-plan result returned alongside a created journey. */
export type AppliedPlanResult = {
  planGroupId: string;
  created: unknown[];
  event: { id: string; name: string } | null;
};

/**
 * The RAW `FuelJourney` row `POST /api/journeys` returns on 201 — the server
 * responds with the created Prisma row as-is (`CreateJourneyBuildResult`,
 * journeys/service.ts), NOT a `JourneyView`: none of the derived view fields
 * (`planStartYmd`, `drops`, `dropStates`, ramp/race header context…) exist on
 * it. Surfaces needing the view must refetch (`notifyJourneyChanged()` →
 * `useJourneys`). Dates cross the wire as ISO strings.
 */
export type CreatedJourneyRow = {
  id: string;
  customerId: string | null;
  anonSessionId: string | null;
  athleteId: string | null;
  customerEventId: string | null;
  planGroupId: string | null;
  trainingPlanId: string | null;
  status: string;
  journeyType: "race" | "rolling";
  /** The stored window-start COLUMN — the view's `planStartYmd` may differ
   *  (null legacy rows resolve at read time, never backfilled). */
  startYmd: string | null;
  raceYmd: string | null;
  /** Rolling journeys: the forward-derivation anchor. Null on race journeys. */
  anchorYmd: string | null;
  dropWeeks: number | null;
  coverageWeeks: number | null;
  skippedDropIndices: number[];
  raceDayPreviewPlanId: string | null;
  customTargets: CustomTargets | null;
  createdAt: string;
  updatedAt: string;
};

export type CreateJourneyResult = {
  journey: CreatedJourneyRow;
  applied: AppliedPlanResult | null;
};

/** The distinct 200 shape the server returns for a `resolveAsMilestone` create
 *  (S1, spec §7.1): NO journey — the new event is stamped `intent:"milestone"`
 *  (or created as one). `event` is null for anon principals (draft-only) and
 *  carries `{id, intent}` — NOT the event name (callers already hold the name
 *  they submitted; the server row shape is `CreateJourneyMilestoneResult`). */
export type CreateMilestoneResult = {
  milestone: true;
  event: { id: string; intent: string } | null;
};

export function isMilestoneResult(
  r: CreateJourneyResult | CreateMilestoneResult
): r is CreateMilestoneResult {
  return (r as CreateMilestoneResult).milestone === true;
}

/**
 * Display name for a race plan. Anon build journeys now carry a real
 * `customerEventId` and a server-side `raceName` (customer-event-anon-ownable),
 * so `journey.raceName` covers most anon journeys directly. The client-held
 * pending-race drafts remain the fallback for the draft-only paths that still
 * create no server event (a milestone/resolve-as-milestone create, or a journey
 * built before its event row lands). Multi-slot (§6.2, Lane D → B3): a guest can
 * hold SEVERAL race drafts at once (a goal + a prep race resolved via the
 * collision sheet), so ALL entries are scanned — a journey is only named by the
 * draft whose planGroupId matches (genuinely the same plan), else by the draft
 * sharing its race DAY, or every anon rail would show one race's name (observed
 * in multi-plan QA).
 */
export function journeyRaceName(
  journey: Pick<JourneyView, "raceName" | "raceYmd" | "journeyType"> & {
    planGroupId?: string | null;
  }
): string {
  // Rolling journeys have no race — every surface names them "Your training".
  if (journey.journeyType === "rolling") return "Your training";
  if (journey.raceName) return journey.raceName;
  const drafts = getAnonPendingRaceEvents();
  // Prefer a planGroupId match: that draft is genuinely the same plan, so a
  // stale draft sharing a race DAY can't mislabel a different plan.
  if (journey.planGroupId) {
    const byGroup = drafts.find(
      (d) => d.planGroupId && d.planGroupId === journey.planGroupId
    );
    if (byGroup) return byGroup.name;
  }
  // Race-day match for group-less drafts (build journeys, legacy drafts) — a
  // draft carrying a DIFFERENT plan's group never falls through to this.
  const byDay = drafts.find((d) => !d.planGroupId && d.dateYmd === journey.raceYmd);
  if (byDay) return byDay.name;
  return "Your race";
}

/** Week-gutter annotation — plain string, or text + color (focused = purple). */
export type WeekAnnotation = string | { text: string; color?: string } | null;

/** Normalize a WeekAnnotation for renderers. */
export function weekAnnotationParts(
  a: WeekAnnotation
): { text: string; color?: string } | null {
  if (a == null) return null;
  return typeof a === "string" ? { text: a } : a;
}

/** Ellipsis-truncate a race name on WORD boundaries to ~`max` chars: keep
 *  whole words while they fit, shed a leading "The" before giving up on the
 *  full name, and don't truncate at all unless it saves ≥3 chars (an ellipsis
 *  that buys back one or two characters reads worse than the overflow). A
 *  single over-budget word hard-slices — nothing else fits. */
export function truncateRaceName(name: string, max = 16): string {
  const full = name.trim().replace(/\s+/g, " ");
  if (full.length <= max + 2) return full;
  const base = full.replace(/^the\s+/i, "");
  if (base.length <= max + 2) return base;
  const words = base.split(" ");
  let out = words[0];
  for (let i = 1; i < words.length; i += 1) {
    if (out.length + 1 + words[i].length > max) break;
    out += ` ${words[i]}`;
  }
  if (out.length > max) out = out.slice(0, max - 1).trimEnd();
  return `${out}…`;
}

/** Short display name for the planner's TIGHT label surfaces — the journey
 *  switcher segments, the multi-plan week-gutter prefix and the provenance
 *  filter option. Word-truncated ({@link truncateRaceName}) so "New Forest
 *  Marathon" reads "New Forest…", not a meaningless first-word "New". The
 *  16-char budget is set by the tightest surface: two switcher segments
 *  (14px/700, nowrap) sharing a ~340px rail. Rolling journeys keep the full
 *  "Your training" (their first-class switcher label). Width-unconstrained
 *  surfaces (tooltips, aria-labels) should use {@link journeyRaceName}. */
export function journeyShortName(
  journey: Pick<JourneyView, "raceName" | "raceYmd" | "journeyType">
): string {
  const name = journeyRaceName(journey);
  if (journey.journeyType === "rolling") return name;
  return truncateRaceName(name);
}

/**
 * Gut-training week label for an arbitrary calendar week, derived ONLY from the
 * server-computed position (F2 discipline — the client never re-derives from a
 * start date): offset `rampWeekNow` by whole plan-grid weeks between the target
 * week and today's week on the same anchor grid, clamped to the span. Null when
 * the ramp is off. NOTE: gut-training weeks are GLOBAL athlete weeks (D9), not
 * plan weeks — the same label shows on every plan's gutter for a given week.
 */
export function gutTrainingWeekFor(
  journey: Pick<
    JourneyView,
    "rampEnabled" | "rampState" | "rampWeekNow" | "rampWeeksTotal" | "rampStartedYmd"
  >,
  weekStartYmd: string,
  todayYmd: string
): { week: number; total: number } | null {
  if (!journey.rampEnabled || journey.rampState == null) return null;
  // Pending = gut training hasn't begun — a week number here would read as a
  // running ramp before the start date. The rail's copy owns that state.
  if (journey.rampState === "pending") return null;
  if (journey.rampWeekNow == null || journey.rampWeeksTotal == null) return null;
  // Offsets MUST ride the ramp's own startedYmd grid — the ramp is athlete-
  // global; a plan-week anchor is per-plan and (mid-plan applies, multi-plan
  // gutters) can disagree with the server's week boundaries by ±1.
  const anchor = journey.rampStartedYmd;
  if (!anchor) return null;
  const gridWeek = (ymd: string) =>
    Math.floor(Math.round((Date.parse(ymd) - Date.parse(anchor)) / 86_400_000) / 7);
  const offset = gridWeek(weekStartYmd) - gridWeek(todayYmd);
  const week = Math.min(journey.rampWeeksTotal, Math.max(1, journey.rampWeekNow + offset));
  return { week, total: journey.rampWeeksTotal };
}

/** GET the active journey + derived drops + per-drop states. Null when none. */
export async function getActiveJourney(signal?: AbortSignal): Promise<JourneyView | null> {
  const res = await apiCall<{ journey: JourneyView | null }>(`${BASE}/active`, { signal });
  return res.journey;
}

/** GET ALL active race plans (one per event), soonest race first. */
export async function getActiveJourneys(signal?: AbortSignal): Promise<JourneyView[]> {
  const res = await apiCall<{ journeys: JourneyView[] }>(BASE, { signal });
  return res.journeys;
}

/**
 * A NON-active (archived / completed) journey's minimal label entry — the
 * calendar provenance filter's archived-plan label map (§5c rework C). The
 * client only receives ACTIVE journeys, so a plan group whose journey has since
 * been archived would otherwise label as a bare "Plan"; this names it.
 * De-duped by planGroupId server-side; may overlap an active journey (the
 * consumer keys the active label first).
 */
export type PlanGroupLabelEntry = {
  planGroupId: string;
  raceName: string | null;
  journeyType: "race" | "rolling";
  /** "archived" | "completed" (never "active" — those arrive as full views). */
  status: string;
};

/**
 * `GET /api/journeys` in full: the active journey views AND the archived-plan
 * label map. `getActiveJourneys` above stays the narrow accessor most callers
 * use; provenance-aware surfaces (the calendar filter) read both here. Older
 * servers omit `planGroupLabels` — it degrades to `[]`.
 */
export async function getActiveJourneysWithLabels(
  signal?: AbortSignal
): Promise<{
  journeys: JourneyView[];
  planGroupLabels: PlanGroupLabelEntry[];
  suppressedGoalEventIds: string[];
}> {
  const res = await apiCall<{
    journeys: JourneyView[];
    planGroupLabels?: PlanGroupLabelEntry[];
    suppressedGoalEventIds?: string[];
    /** Pre-rename servers (journey-focus §3.2) — same ids, old key. */
    endedGoalEventIds?: string[];
  }>(BASE, { signal });
  return {
    journeys: res.journeys,
    planGroupLabels: res.planGroupLabels ?? [],
    suppressedGoalEventIds: res.suppressedGoalEventIds ?? res.endedGoalEventIds ?? []
  };
}

/**
 * Run a journey read with short retries. A transient failure (dev-server
 * restart, flaky network) must NEVER make an existing race plan disappear from
 * the UI — surfaces retry a couple of times before surfacing an error state.
 * Abort (unmount/navigation) rethrows immediately and is never retried.
 */
export async function withJourneyRetries<T>(
  fn: () => Promise<T>,
  signal?: AbortSignal,
  delaysMs: number[] = [600, 1800]
): Promise<T> {
  let lastErr: unknown;
  for (let attempt = 0; attempt <= delaysMs.length; attempt += 1) {
    try {
      return await fn();
    } catch (e) {
      if (isAbortError(e) || signal?.aborted) throw e;
      // 4xx (bar timeout/rate-limit) is a definitive answer — retrying won't
      // change a 404/403; rethrow so callers can branch on it immediately.
      const status = (e as { status?: number }).status;
      if (status != null && status >= 400 && status < 500 && status !== 408 && status !== 429) {
        throw e;
      }
      lastErr = e;
      if (attempt < delaysMs.length) {
        await new Promise((r) => setTimeout(r, delaysMs[attempt]));
        if (signal?.aborted) throw lastErr;
      }
    }
  }
  throw lastErr;
}

/** GET one journey by id (any status) — event-scoped URLs + context bars. */
export async function getJourneyView(
  journeyId: string,
  signal?: AbortSignal
): Promise<JourneyView | null> {
  const res = await apiCall<{ journey: JourneyView | null }>(
    `${BASE}/${encodeURIComponent(journeyId)}/view`,
    { signal }
  );
  return res.journey;
}

/** Create a journey (single wizard-apply entry). Archives any active one first. */
export async function createJourney(
  body: CreateJourneyBody,
  csrfToken: string,
  refreshCsrf: () => Promise<string>
): Promise<CreateJourneyResult> {
  return apiCallWithCsrfRefresh<CreateJourneyResult>(
    BASE,
    { method: "POST", body, csrfToken },
    refreshCsrf
  );
}

/** Create variant for the `nest_new` retry (S1/C2): the server may answer a
 *  `resolveAsMilestone` create with `{ milestone: true, event }` instead of a
 *  journey, so the caller must handle the union (`isMilestoneResult`). */
export async function createJourneyOrMilestone(
  body: CreateJourneyBody,
  csrfToken: string,
  refreshCsrf: () => Promise<string>
): Promise<CreateJourneyResult | CreateMilestoneResult> {
  return apiCallWithCsrfRefresh<CreateJourneyResult | CreateMilestoneResult>(
    BASE,
    { method: "POST", body, csrfToken },
    refreshCsrf
  );
}

/** Monotonic count of `notifyJourneyChanged` calls this page-load. Listeners
 *  can miss the event itself (a mutation fires notify → navigate before the
 *  next surface's `useJourneys` attaches its listener, or React defers the
 *  attach past the dispatch) — the stamp lets a hook detect that its current
 *  fetch started before the latest change and refetch (wizard-apply →
 *  `/my-training` rendered the stale no-journey shell). */
let journeyChangeCount = 0;

export function journeyChangeStamp(): number {
  return journeyChangeCount;
}

/** Dispatch a cross-component "journey changed" signal so the rail refetches. */
export function notifyJourneyChanged(): void {
  journeyChangeCount += 1;
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("fg:journey-changed"));
  }
}

export const JOURNEY_CHANGED_EVENT = "fg:journey-changed";

/** Patch a journey (settings). */
export async function patchJourney(
  journeyId: string,
  patch: JourneyPatch,
  csrfToken: string,
  refreshCsrf: () => Promise<string>
): Promise<JourneyView> {
  return apiCallWithCsrfRefresh<JourneyView>(
    `${BASE}/${encodeURIComponent(journeyId)}`,
    { method: "PATCH", body: patch, csrfToken },
    refreshCsrf
  );
}

/**
 * Generate (or reuse) a drop's fueling plan.
 *
 * `email` (Contract C) carries an ANON visitor's lead email so the server can
 * fire the same Klaviyo lead-capture the v3 planner generate does. Only sent
 * when present (anon principals): a customer principal omits it and the server
 * reads `Customer.email`. Older servers strip the unknown key (zod), so
 * sending it is safe before the Lane 7 server side lands.
 */
export async function generateDrop(
  journeyId: string,
  dropIndex: number,
  csrfToken: string,
  refreshCsrf: () => Promise<string>,
  email?: string,
  force?: boolean,
  discardCustomizations?: boolean
): Promise<JourneyGenerateResult> {
  // `force` (regenerate a stale box) rides in the POST body — the same
  // convention as the milestone kit. It skips the server's slot-resident reuse
  // and mints a fresh regenerationToken, so the rebuild actually dispatches
  // instead of echoing the existing (stale) plan back as `reused`.
  // `discardCustomizations` (F-11-1) is sent only after the customer confirmed
  // the rebuild on a box carrying edits — without it the server refuses with
  // `409 source_has_customizations`.
  const body = generateBodyWithAnonExtras(email, force, discardCustomizations);
  return apiCallWithCsrfRefresh<JourneyGenerateResult>(
    `${BASE}/${encodeURIComponent(journeyId)}/drops/${dropIndex}/generate`,
    { method: "POST", csrfToken, ...(body ? { body } : {}) },
    refreshCsrf
  );
}

/**
 * Generate (or reuse) the free race-day preview. `email` (Contract C) — see
 * {@link generateDrop}: anon lead capture only, customer omits.
 */
export async function generateRaceDayPreview(
  journeyId: string,
  csrfToken: string,
  refreshCsrf: () => Promise<string>,
  email?: string,
  force?: boolean
): Promise<JourneyGenerateResult> {
  // `force` (regenerate a stale kit) — see {@link generateDrop}. Without it an
  // in-place edit to the race-day workout (same row ID) dedups back to the
  // stale kit at the server's idempotency key.
  const body = generateBodyWithAnonExtras(email, force);
  return apiCallWithCsrfRefresh<JourneyGenerateResult>(
    `${BASE}/${encodeURIComponent(journeyId)}/race-day-preview/generate`,
    { method: "POST", csrfToken, ...(body ? { body } : {}) },
    refreshCsrf
  );
}

/**
 * Generate (or reuse) a MILESTONE tune-up race-day kit (P5, D1) —
 * `POST /:id/milestones/:eventId/kit`. Same generate result/budget as the
 * goal preview; the kit lands stamped with the milestone's event so it
 * coexists with the goal kit. 422 `milestone_invalid` when the event isn't a
 * milestone inside the journey window. `email` (Contract C) — see
 * {@link generateDrop}: anon lead capture only, customer omits.
 */
export async function generateMilestoneKit(
  journeyId: string,
  eventId: string,
  csrfToken: string,
  refreshCsrf: () => Promise<string>,
  email?: string,
  force?: boolean
): Promise<JourneyGenerateResult> {
  // `force` (regenerate a stale kit) rides in the POST body — the same
  // convention `email` uses. An older server strips the unknown key via zod, and
  // the changed workout/prefs set already mints a fresh idempotency key, so the
  // flag is advisory + forward-compatible with the milestone-staleness lane.
  const body = generateBodyWithAnonExtras(email, force);
  return apiCallWithCsrfRefresh<JourneyGenerateResult>(
    `${BASE}/${encodeURIComponent(journeyId)}/milestones/${encodeURIComponent(eventId)}/kit`,
    { method: "POST", csrfToken, ...(body ? { body } : {}) },
    refreshCsrf
  );
}

/** Race-day kit (goal preview / milestone) generate failure codes: the route
 *  contracts are `404 preview_not_generatable` / `404 kit_not_generatable`
 *  (missing journey/event, rolling, anon milestone), `422 milestone_invalid`
 *  (not a milestone / outside the window) and `400 email_required` (E-03-1 anon
 *  lead gate). Null = unclassified (network/5xx). */
export type KitGenerateErrorCode =
  | "preview_not_generatable"
  | "kit_not_generatable"
  | "milestone_invalid"
  | "email_required";

export function kitGenerateErrorCode(err: unknown): KitGenerateErrorCode | null {
  if (!(err instanceof ApiError)) return null;
  if (
    err.code === "preview_not_generatable" ||
    err.code === "kit_not_generatable" ||
    err.code === "milestone_invalid" ||
    // E-03-1: the anon lead gate answers 400 on every journey generate surface.
    err.code === "email_required"
  ) {
    return err.code;
  }
  return null;
}

/** Persist a skip-ahead. */
export async function skipDrop(
  journeyId: string,
  dropIndex: number,
  csrfToken: string,
  refreshCsrf: () => Promise<string>
): Promise<JourneyView> {
  return apiCallWithCsrfRefresh<JourneyView>(
    `${BASE}/${encodeURIComponent(journeyId)}/drops/${dropIndex}/skip`,
    { method: "POST", csrfToken },
    refreshCsrf
  );
}
